import LoadingAnimation from '@/components/LoadingAnimation/LoadingAnimation'
import { CostCategoryPreviewRow } from '@/app/pages/utility-program/unenroll/suggestions/components/ThermostatSuggestionCard/ThermostatSuggestionCard.components'
import Separator from '@/app/components/Separator/Separator'
import { Text } from '@/components'
import { findBrandByPortal } from '@/types/thermostatBrands'
import ConfigureDeviceStepBody from '../components/ConfigureDeviceStepBody'
import ConfigureDeviceCard from '../components/ConfigureDeviceCard'
import ConfigureSettingRow from '../components/ConfigureSettingRow'
import ProgramPrescribedAlert from '../components/ProgramPrescribedAlert'
import checkUserCanAccessTstatSaveMoney from '@/app/pages/thermostat/cost-categories/utils/checkUserCanAccessTstatSaveMoney'
import useConfigureThermostatViewModel from './ConfigureThermostat.viewmodel'

export default function ConfigureThermostat() {
  const {
    isLoadingOrIdle,
    thermostat,
    displayName,
    settingPaths,
    costCategories,
    inactiveTimesSubtitleLines,
    gridSupportEnabled,
    gridSupportSummary,
    precoolEnabled,
    saveMoneyPrescribed,
    saveMoneyEnabled,
    saveMoneyRequiredDirection,
  } = useConfigureThermostatViewModel()

  if (isLoadingOrIdle) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingAnimation type="falling" />
      </div>
    )
  }

  const subtitle = thermostat?.portal
    ? `${thermostat.portal} Thermostat`
    : 'Thermostat'
  // Reuse the same portal→deviceImage mapping the device-selection gallery
  // uses (`/img/<brand>.webp`). Fallback to nest if the portal is unknown,
  // matching the existing convention in useSelectionGalleryResources.
  const brandImage =
    (thermostat?.portal && findBrandByPortal(thermostat.portal)?.deviceImage) ??
    '/img/nest.webp'
  const illustration = (
    <img
      src={brandImage}
      alt={subtitle}
      className="h-16 w-auto object-contain"
    />
  )

  return (
    <ConfigureDeviceStepBody>
      <ConfigureDeviceCard
        title={displayName}
        subtitle={subtitle}
        illustration={illustration}
      >
        <ConfigureSettingRow
          iconName="Globe"
          title="Grid Support"
          description="We'll adjust your temperature based on your set temp range for DR events"
          destinationPath={settingPaths.gridSupport}
        >
          {gridSupportEnabled && gridSupportSummary.length > 0 ? (
            <div className="flex flex-col gap-1">
              {gridSupportSummary.map((setting) => (
                <div key={setting.label ?? setting.description}>
                  {setting.label && (
                    <Text
                      variant="body3"
                      className="font-bold text-themed-grey-700"
                    >
                      {setting.label}
                    </Text>
                  )}
                  <span>{setting.description}</span>
                </div>
              ))}
            </div>
          ) : (
            'Disabled'
          )}
        </ConfigureSettingRow>

        <ConfigureDeviceCard.Divider />

        <ConfigureSettingRow
          iconName="Wind"
          title="Pre-cool"
          description="We'll cool your home down to your desired temperature when electricity rates are cheapest"
          destinationPath={settingPaths.precool}
        >
          {precoolEnabled ? 'Enabled' : 'Disabled'}
        </ConfigureSettingRow>

        {checkUserCanAccessTstatSaveMoney(costCategories) && (
          <>
            {saveMoneyPrescribed ? (
              <ProgramPrescribedAlert
                settingDisplayName="Save Money"
                requiredDirection={saveMoneyRequiredDirection}
              />
            ) : (
              <ConfigureDeviceCard.Divider />
            )}

            <ConfigureSettingRow
              iconName="DollarSign"
              title="Save Money"
              description="We'll check rates hourly and shift your temperature based on your preferences"
              destinationPath={settingPaths.costCategories}
            >
              <div className="flex flex-col">
                <div className="flex flex-col gap-1">
                  <CostCategoryPreviewRow
                    type="negative"
                    costCategories={costCategories}
                  />
                  <CostCategoryPreviewRow
                    type="low"
                    costCategories={costCategories}
                  />
                  <CostCategoryPreviewRow
                    type="medium"
                    costCategories={costCategories}
                  />
                  <CostCategoryPreviewRow
                    type="high"
                    costCategories={costCategories}
                  />
                </div>
                <Separator className="my-2 bg-themed-grey-200" />
                <Text
                  variant="body3"
                  className="font-bold text-themed-grey-700"
                >
                  Inactive Times:
                </Text>
                {inactiveTimesSubtitleLines.length > 0 ? (
                  inactiveTimesSubtitleLines.map((line) => (
                    <span key={line}>{line}</span>
                  ))
                ) : (
                  <span>No times set</span>
                )}
              </div>
            </ConfigureSettingRow>
          </>
        )}
      </ConfigureDeviceCard>
    </ConfigureDeviceStepBody>
  )
}
